<script lang="ts" setup>
import { ref } from 'vue';

import { formatValue } from '@/utils/helpers';
import { computed, useDevice, useProviderConfig } from '#imports';
import type { EvmItemType } from '~/service/EVM/types';

interface PropsCard {
    img?: string;
    title: string;
    uuid: string;
    network: User['network'];
    price?: AppWTFNft['price'];
    address?: AppWTFNft['address'];
    sizes?: string;
    marketplace?: EvmMarketplace;
    itemType: EvmItemType;
    isExistToCart?: boolean;
    ownedCount: AppWTFNft['ownedCount'];
    collectionSlug: LiteNft['collection']['slug'];
    identifier: AppWTFNft['identifier'];
    gridChange?: boolean;
    type?: 'collection' | 'profile';
}

const props = withDefaults(
    defineProps<PropsCard>(),
    {
        isExistToCart: false,
        price: undefined,
        type: 'collection',
        marketplace: 'default',
        address: undefined,
        sizes: undefined
    }
);

const emit = defineEmits<{
    (e: 'handleCart'): void;
    (e: 'buyNow'): void;
    (e: 'makeOffer'): void;
    (e: 'openListing'): void;
}>();

const { utils: { isEvmErc1155 } } = useProviderConfig()(['utils']);

const showButtons = ref(false);
const formattedPrice = formatValue(
    props.price?.amount as number,
    { precision: 6 }
);
const isVideo = computed(() => props.img && (/\.(mp4|webm|ogg)$/i).test(props.img) ? (/\.(mp4|webm|ogg)$/i).test(props.img) : '');
const isListed = computed(() => props.price?.amount && props.price?.amount > 0);
const is1155 = computed(() => props.itemType && isEvmErc1155(props.itemType));

const { isMobileOrTablet } = useDevice();
const buttonSize = computed(() => props.gridChange ? 'size.small' : 'size.medium');
const textClass = computed(() => props.gridChange ? 'compact-400-accent' : 'compact-500-accent');

</script>

<template>
  <article :class="[{ 'nft-small': gridChange }, { 'nft-container--has-cart': isExistToCart }]"
           class="display-flex flex-column align-items-start nft-container gap-16 width-full"
           @mouseleave="showButtons = false"
           @mouseover="showButtons = true"
  >
    <NuxtLink :to="{
                name: 'collections-collection-identifier',
                params: { collection: props.collectionSlug, identifier:props.identifier}}"
              class="width-full height-full position-relative"
              prefetch-on="interaction"
              draggable="false"
    >
      <video v-if="!!isVideo"
             :autoplay="!isMobileOrTablet"
             class="nft-img height-full"
             controls
             loop
             muted
      >
        <source :src="props.img">
      </video>
      <ImgMain v-else-if="props.img"
               :alt="props.title"
               :sizes="sizes"
               :src="props.img"
               class="nft-img"
               loading="lazy"
      />
      <Transition v-if="type === 'collection'"
                  name="slide-fade"
      >
        <div v-if="showButtons"
             class="nfts-buttons position-absolute display-flex align-items-center width-full"
        >
          <ClientOnly v-if="isListed">
            <Web3BuyNowButton v-if="!is1155"
                              :button-class="['text-wrap-none', textClass]"
                              :item="{ uuid, name: title, price, displayImageURL: img, network, itemType }"
                              :size="buttonSize"
                              class="nft-container-btn__buy"
            >
              <span>Buy now</span>
            </Web3BuyNowButton>
            <Button :class="[
                      {'nft-container-btn__cart': !is1155},
                      { 'nft-container-btn__cart--small': gridChange }]"
                    :color="'bg.mojito'"
                    :size="buttonSize"
                    circle
                    class="width-full"
            >
              <button aria-label="cart"
                      class="display-flex align-items-center justify-center"
                      type="button"
                      @click.prevent="emit('handleCart')"
                      @click.stop
              >
                <Transition mode="out-in"
                            name="fade"
                >
                  <IconMain v-if="isExistToCart"
                            :size="gridChange ? 16 : 24"
                            icon="basket"
                  />
                  <IconMain v-else
                            :size="gridChange ? 16 : 24"
                            icon="cart"
                  />
                </Transition>
                <template v-if="is1155">
                  <Transition mode="out-in"
                              name="fade"
                  >
                    <span v-if="isExistToCart"
                          :class="textClass"
                    >
                      Remove
                    </span>
                    <span v-else
                          :class="textClass"
                    >
                      To cart
                    </span>
                  </Transition>
                </template>
              </button>
            </Button>
          </ClientOnly>
          <Button v-else-if="address && itemType && price"
                  circle
                  class="width-full"
                  color="bg.mojito"
                  :size="buttonSize"
                  @click.prevent="emit('makeOffer')"
          >
            <button :class="textClass"
                    type="button"
            >
              Make offer
            </button>
          </Button>
          <Button v-else-if="props.ownedCount"
                  circle
                  class="width-full"
                  color="bg.mojito"
                  :size="buttonSize"
                  @click.prevent="emit('openListing')"
          >
            <button :class="textClass"
                    type="button"
            >
              To listed
            </button>
          </Button>
        </div>
      </Transition>
    </NuxtLink>

    <div class="display-flex flex-column width-full justify-between gap-8">
      <h3 :class="[{ 'grid-change': gridChange }]"
          class="compact-600-accent nft-container__title"
      >
        {{ props.title }}
      </h3>

      <div v-if="type === 'collection'"
           class="display-flex justify-between align-items-center"
      >
        <div class="display-flex flex-column align-self-start width-full gap-4">
          <p v-if="formattedPrice"
             class="compact-300-accent text-color text-start"
          >
            Price
          </p>

          <p v-if="formattedPrice"
             class="compact-400-numeric display-flex align-items-center gap-4"
          >
            <span class="svg-bage display-flex align-items-center justify-center">
              <ImgMain v-if="props.price?.logo"
                       :alt="props.price.token"
                       :src="props.price.logo"
              />
              <IconMain v-else
                        :size="16"
                        icon="etherium"
              />
            </span>
            <span class="nft-container-card-price__text">{{ formattedPrice }}</span>
          </p>
          <p v-else
             class="compact-400-numeric nft-container__not-listed"
          >
            Not listed
          </p>
        </div>

        <div v-show="!gridChange"
             class="display-flex flex-column align-self-end width-full gap-4"
        >
          <p class="compact-300-accent text-color text-end">
            Highest bid
          </p>

          <p class="compact-400-numeric text-end">
            No bids
          </p>
        </div>
      </div>
    </div>
  </article>
</template>

<style lang="scss">

.nft-container {
  box-sizing: border-box;
  align-self: stretch;
  padding: 8px;
  transition: all 0.3s ease-in-out;
  border: 1px solid transparent;
  border-radius: var(--radius-100, 4px);
  background: var(--layer-floor-1);

  &-btn {
    &__buy {
      flex-grow: 1;

      button:first-child {
        padding: 0;
      }
    }

    &__cart {
      width: 59px;
      height: 59px;

      button:first-child {
        padding: 0;
      }

      &--small {
        width: 36px;
        height: 36px;
      }
    }
  }

  &__title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__not-listed {
    margin-top: revert;
  }

  &--has-cart {
    border: 1px solid var(--bg-brand-mojito-primary);
  }

  &-card-price {
    width: 50%;

    &__text {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &:hover {
    background: var(--layer-floor-2);
  }
}

.nft-img {
  width: 100%;
  transition: opacity 0.3s ease-in-out;
  object-fit: cover;
  aspect-ratio: 1;

  &-backing {
    position: absolute;
    z-index: -1;
    left: 0;
    width: 100%;
    height: 100%;
  }
}

.nft-container:hover .nft-img {
  opacity: 0.4;
}

.text-color {
  color: var(--content-secondary);
}

.svg-bage {
  width: 22px;
  height: 22px;
  border-radius: var(--radius-999, 999px);
  background: var(--bg-secondary);
}

.nfts-buttons {
  bottom: 10px;
}

.btn-buy {
  height: 52px;
  padding: 0 30px;
  border-radius: var(--radius-999, 999px);
  background: var(--bg-brand-aviation-primary);
  color: var(--content-primary);
  overflow-wrap: normal;
}
</style>

<style lang="scss" scoped>
.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.8s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(20px);
  opacity: 0;
}

.fade-enter-active {
  transition: all 0.3s ease-out;
}

.fade-leave-active {
  transition: all 0.3s cubic-bezier(1, 0.8, 0.9, 1);
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
